import { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Lock, Save, Download, FileText } from "lucide-react";
import { toast } from "sonner";

export default function AccountPage() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 6) {
      toast.error("La nueva contraseña debe tener al menos 6 caracteres");
      return;
    }
    if (form.new_password !== form.confirm) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Contraseña actualizada");
      setForm({ current_password: "", new_password: "", confirm: "" });
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8 lg:p-12 max-w-[800px]" data-testid="account-page">
      <header className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3">Mi cuenta</div>
        <h1 className="font-display text-4xl font-bold tracking-tighter text-slate-950">Datos y seguridad</h1>
      </header>

      <div className="bg-white border border-slate-200 rounded-lg p-6 mb-6" data-testid="account-info">
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3">Datos de la cuenta</div>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <Row label="Farmacia" value={user?.pharmacy_name || user?.name} />
          <Row label="Código de licencia" value={user?.license_code || "—"} />
          <Row label="Email" value={user?.email} />
          <Row label="Teléfono" value={user?.phone || "—"} />
          <Row label="Provincia" value={user?.province || "—"} />
          <Row label="CCAA" value={user?.ccaa || "—"} />
          <Row label="Plan" value={planLabel(user?.license_type)} />
          <Row label="Estudios subidos" value={user?.studies_used ?? 0} />
        </dl>
      </div>

      {user?.must_change_password && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 text-sm text-amber-800">
          ⚠️ Te recomendamos <strong>cambiar la contraseña inicial</strong> que recibiste por una propia.
        </div>
      )}

      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-lg p-6 space-y-4" data-testid="change-password-form">
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2 flex items-center gap-2">
          <Lock className="h-3 w-3" /> Cambiar contraseña
        </div>
        <Input label="Contraseña actual" type="password" value={form.current_password} onChange={(v) => setForm({ ...form, current_password: v })} testid="cp-current" />
        <Input label="Nueva contraseña" type="password" value={form.new_password} onChange={(v) => setForm({ ...form, new_password: v })} testid="cp-new" />
        <Input label="Repite la nueva contraseña" type="password" value={form.confirm} onChange={(v) => setForm({ ...form, confirm: v })} testid="cp-confirm" />
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            data-testid="cp-submit"
            className="inline-flex items-center gap-1.5 bg-slate-950 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-800 disabled:opacity-50"
          >
            <Save className="h-3.5 w-3.5" />
            {saving ? "Guardando…" : "Guardar contraseña"}
          </button>
        </div>
      </form>

      <div className="bg-white border border-slate-200 rounded-lg p-6 mt-6" data-testid="downloads-section">
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3 flex items-center gap-2">
          <Download className="h-3 w-3" /> Descargas y ayuda
        </div>
        <a
          href={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/manual/download`}
          download="FarmaClass-Manual-Usuario.pdf"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="download-manual"
          className="flex items-center gap-3 p-4 border border-slate-200 rounded-md hover:border-slate-900 hover:bg-slate-50 transition-colors group"
        >
          <div className="h-10 w-10 rounded-md bg-rose-50 grid place-items-center text-rose-700 shrink-0 group-hover:bg-rose-100">
            <FileText className="h-5 w-5" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-slate-900 text-sm">Manual de usuario FarmaClass (PDF · 49 páginas)</div>
            <div className="text-xs text-slate-500">
              Guía completa con capturas reales: acceso, dispensación, semáforo, Top 25 «Vs Mercado», proveedores, pedidos, incentivos y empleados.
            </div>
          </div>
          <Download className="h-4 w-4 text-slate-400 group-hover:text-slate-900 transition-colors shrink-0" />
        </a>
        <div className="text-xs text-slate-500 mt-3">
          ¿Necesitas formación con tu equipo? Llámanos al{" "}
          <a href="tel:+34609467788" className="font-semibold text-slate-900 hover:underline">609 46 77 88</a> o escribe a{" "}
          <a href="mailto:pedro@archiduquecarlos.com" className="font-semibold text-slate-900 hover:underline">pedro@archiduquecarlos.com</a>.
        </div>
      </div>
    </div>
  );
}

function planLabel(t) {
  if (t === "single_use") return "Uso único";
  if (t === "subscription_monthly") return "Suscripción mensual";
  if (t === "subscription_annual") return "Suscripción anual";
  return "—";
}

function Row({ label, value }) {
  return (
    <>
      <dt className="text-slate-500 text-xs uppercase tracking-wider">{label}</dt>
      <dd className="text-slate-900 font-medium">{value}</dd>
    </>
  );
}

function Input({ label, type = "text", value, onChange, testid }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{label}</div>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
        required
      />
    </label>
  );
}
